import morph from "../helpers/morph";

const PREFETCH_TTL = 30000;
const HOVER_DELAY = 80;
const DECODE_TIMEOUT = 500;
const LEAD_IMAGE_SELECTOR = "media-gallery [data-media]:first-child picture img";

// Shared by prefetch and selection, so a selection reuses a prefetch still in flight
const sectionRequests = new Map();

// Held so the detached images' loads aren't dropped
const warmedImages = new Map();

function loadSection(url) {
  const key = url.toString();
  const cached = sectionRequests.get(key);

  if (cached && cached.expires > Date.now()) return cached.request;

  const request = fetch(url).then((res) => {
    if (!res.ok) throw new Error(`${res.status} loading ${key}`);
    return res.text();
  });
  const entry = { request, expires: Date.now() + PREFETCH_TTL };

  sectionRequests.set(key, entry);
  request.catch(() => {
    if (sectionRequests.get(key) === entry) sectionRequests.delete(key);
  });

  return request;
}

/**
 * The file name and version of a Shopify image URL — the part shared by the
 * Storefront API's CDN URL and the theme's `image_url` output.
 */
const imageKey = (url) => {
  const { pathname, searchParams } = new URL(url, window.location.href);
  return `${pathname.split("/").pop()}?v=${searchParams.get("v")}`;
};

const timeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class ProductPageMorph extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("variant:changed", (e) => {
      const { variant } = e.detail;

      this._selectedVariant = variant;

      const url =
        variant && variant.id !== this.renderedVariantId
          ? this._sectionUrl(variant)
          : null;

      // Also set when returning to the rendered variant, so a swap still in
      // flight for another one is dropped
      this._target = url?.toString();

      if (url) this._update(url, variant);
    });

    this.addEventListener("pointerover", (e) => {
      const input = this._optionInput(e.target);

      if (input === this._hoveredInput) return;

      clearTimeout(this._hoverTimer);
      this._hoveredInput = input;

      if (!input) return;

      // Touch fires pointerover at the start of a tap, so there's no time to wait
      if (e.pointerType === "mouse") {
        this._hoverTimer = setTimeout(() => this._prefetch(input), HOVER_DELAY);
      } else {
        this._prefetch(input);
      }
    });

    this.addEventListener("pointerleave", () => {
      clearTimeout(this._hoverTimer);
      this._hoveredInput = null;
    });

    this.addEventListener("focusin", (e) => {
      const input = this._optionInput(e.target);

      if (input) this._prefetch(input);
    });
  }

  connectedCallback() {}

  disconnectedCallback() {}

  async _update(url, variant) {
    const target = url.toString();
    const leadImage = this._warmLeadImage(variant);
    const request = loadSection(url);

    // The next visit should get fresh stock data
    sectionRequests.delete(target);

    const html = await request;

    if (this._target !== target) return;

    const incoming = document.createRange().createContextualFragment(html);

    if (leadImage) {
      await Promise.race([
        leadImage.decode().catch(() => {}),
        timeout(DECODE_TIMEOUT),
      ]);

      if (this._target !== target) return;
    }

    if (this.updateUrl) {
      url.searchParams.delete("section_id");
      window.history.replaceState("", "", url);
    }

    morph(this._section, incoming);
  }

  _prefetch(input) {
    if (navigator.connection?.saveData) return;

    const variant = this._variantWith(input);

    if (!variant || variant.id === this.renderedVariantId) return;

    this._warmLeadImage(variant);
    loadSection(this._sectionUrl(variant));
  }

  /**
   * Starts loading the gallery's incoming lead image from client-side variant
   * data, before the section HTML arrives.
   * @returns {HTMLImageElement|null} detached copy to wait on, when the lead image changes
   */
  _warmLeadImage(variant) {
    const url = variant?.image?.url;
    const img = this._section.querySelector(LEAD_IMAGE_SELECTOR);

    if (!url || !img) return null;

    const from = imageKey(img.src);
    const to = imageKey(url);

    if (from === to) return null;
    if (warmedImages.has(to)) return warmedImages.get(to);

    // Copying the live <picture> keeps every source, width and media query,
    // so the copy picks the same URL the incoming gallery will request
    const picture = img.closest("picture").cloneNode(true);

    picture.querySelectorAll("source, img").forEach((el) => {
      ["srcset", "src"].forEach((attribute) => {
        const value = el.getAttribute(attribute);
        if (value) el.setAttribute(attribute, value.replaceAll(from, to));
      });
    });

    const warmed = picture.querySelector("img");
    warmedImages.set(to, warmed);

    return warmed;
  }

  /**
   * The variant that choosing this option input would select, keeping the
   * other options as they are.
   */
  _variantWith(input) {
    const product = this._selectedVariant?.product;

    if (!product) return null;

    const optionName = input.name.slice(this._idPrefix.length);
    const selection = Object.fromEntries(
      this._selectedVariant.selectedOptions.map(({ name, value }) => [
        name,
        value,
      ]),
    );

    selection[optionName] = input.value;

    return (
      product.variants.find(({ selectedOptions }) =>
        selectedOptions.every(({ name, value }) => selection[name] === value),
      ) || null
    );
  }

  _optionInput(target) {
    const input = target
      .closest?.(".product-options label")
      ?.querySelector('input[type="radio"]');

    return input && !input.checked && !input.disabled ? input : null;
  }

  _sectionUrl(variant) {
    const url = new URL(
      window.productUrl(variant.product.handle),
      window.location.origin,
    );

    url.searchParams.set("variant", window.gidToId(variant.id));
    url.searchParams.set(
      "section_id",
      this._section.id.replace("shopify-section-", ""),
    );

    return url;
  }

  get renderedVariantId() {
    return this.getAttribute("rendered-variant-id");
  }

  get updateUrl() {
    return !window.Shopify.designMode && this.hasAttribute("update-url");
  }

  get _section() {
    return this.closest(".shopify-section");
  }

  get _idPrefix() {
    return this.querySelector("product-form")?.getAttribute("id-prefix") || "";
  }
}

if (!customElements.get("product-page-morph")) {
  customElements.define("product-page-morph", ProductPageMorph);
}
